import { ArrowDownRight, ArrowRight, ArrowUpRight } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { Badge } from '../ui';
import type { MetricTrend } from '../../../lib/site-analytics';
import styles from './SiteFigures.module.css';

// The small figure pieces the site strip and the Analytics page share: the trend chip and the number formatters.

const formatters = new Map<string, Intl.NumberFormat>();

/** One cached Intl.NumberFormat per (locale, options) — never built in a render path. */
function formatter(locale: string, key: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const id = `${locale}|${key}`;
  let f = formatters.get(id);
  if (!f) {
    try {
      f = new Intl.NumberFormat(locale, options);
    } catch {
      f = new Intl.NumberFormat(locale, key.startsWith('money') ? { maximumFractionDigits: 2 } : {});
    }
    formatters.set(id, f);
  }
  return f;
}

/** A count: grouped, compact from 10K up (12.9K). */
export function formatCount(n: number, locale: string): string {
  return n >= 10_000
    ? formatter(locale, 'compact', { notation: 'compact', maximumFractionDigits: 1 }).format(n)
    : formatter(locale, 'count', { maximumFractionDigits: 0 }).format(n);
}

/** Sales: with the site's currency when known, else a plain number (never a guessed symbol). */
export function formatMoney(n: number, currency: string, locale: string, compact = n >= 10_000): string {
  if (!currency) return formatter(locale, `money|plain|${compact}`, compact ? { notation: 'compact', maximumFractionDigits: 1 } : { maximumFractionDigits: 2 }).format(n);
  return formatter(locale, `money|${currency}|${compact}`, {
    style: 'currency',
    currency,
    ...(compact ? { notation: 'compact', maximumFractionDigits: 1 } : {}),
  }).format(n);
}

/** The trend in words, for an accessible name: "up 12%", "down 4%", "no change". */
export function useTrendWords() {
  const { t } = useTranslation();
  return (trend: MetricTrend | null) =>
    !trend ? '' : trend.direction === 'flat' ? t('siteAnalytics.trend.flat') : t(`siteAnalytics.trend.${trend.direction}`, { percent: String(trend.percent) });
}

/** ▲ green / ▼ red / → neutral: the DS Badge (done / failed / the neutral grey) with an arrow and the percent — an icon
 * and a number, never colour alone — plus the trend in words for a screen reader. */
export function TrendChip({ trend, size = 'small' }: { trend: MetricTrend; size?: 'small' | 'tiny' }) {
  const words = useTrendWords();
  const Icon = trend.direction === 'up' ? ArrowUpRight : trend.direction === 'down' ? ArrowDownRight : ArrowRight;
  const variant = trend.direction === 'up' ? 'done' : trend.direction === 'down' ? 'failed' : 'waiting-on-a-reply';
  return (
    <Badge
      variant={variant}
      size={size}
      title={words(trend)}
      data-trend={trend.direction}
      className={styles.chip}
      startIcon={<Icon size={14} aria-hidden className="dir-flip" />}
      label={
        <>
          <span aria-hidden>{trend.percent}%</span>
          <span className={styles.srOnly}>{words(trend)}</span>
        </>
      }
    />
  );
}
